"use client";

import { c as _c } from "react/compiler-runtime";
import type { FooterNavigationBadge, FooterNavigationItemWithMarket } from "./types";
import { useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Link } from "@local/ui/src/components/ui/Link";
import { Modal } from "@local/ui/src/components/ui/Modal";
import { withReturnPath } from "@local/utils/src/returnPath";
import dynamic from "next/dynamic";
import { CookiePreferencesButton } from "./CookiePreferencesButton";
import { getT } from "@local/i18n/src/t";
import { getLanguageForMarket, localizeMarketPath, marketBaseOf } from "@local/i18n/src/languages";
import { footer } from "@local/i18n/src/catalogs/footer";
import { a11y } from "@local/i18n/src/catalogs/a11y";
const BadgeImage = t0 => {
  const $ = _c(5);
  const {
    badge
  } = t0;
  let t1;
  if ($[0] !== badge.alt || $[1] !== badge.height || $[2] !== badge.src || $[3] !== badge.width) {
    t1 = <Image src={badge.src} alt={badge.alt} width={badge.width} height={badge.height} aria-hidden="true" className="h-4 w-auto" />;
    $[0] = badge.alt;
    $[1] = badge.height;
    $[2] = badge.src;
    $[3] = badge.width;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

// Dynamically import static page components for modal display
const AboutUs = dynamic(() => import("../StaticPages/AboutUs").then(mod => mod.AboutUs), {
  ssr: false
});
const VeteransCharities = dynamic(() => import("../StaticPages/VeteransCharities").then(mod => mod.VeteransCharities), {
  ssr: false
});
const BaerskinStories = dynamic(() => import("../StaticPages/BaerskinStories").then(mod => mod.BaerskinStories), {
  ssr: false
});
const PrivacyPolicy = dynamic(() => import("../StaticPages/PrivacyPolicy").then(mod => mod.PrivacyPolicy), {
  ssr: false
});
const TermsConditions = dynamic(() => import("../StaticPages/TermsConditions").then(mod => mod.TermsConditions), {
  ssr: false
});
const ShippingReturnsRefunds = dynamic(() => import("../StaticPages/ShippingReturnsRefunds").then(mod => mod.ShippingReturnsRefunds), {
  ssr: false
});
const SecurePayments = dynamic(() => import("../StaticPages/SecurePayments").then(mod => mod.SecurePayments), {
  ssr: false
});
const Accessibility = dynamic(() => import("../StaticPages/Accessibility").then(mod => mod.Accessibility), {
  ssr: false
});

/**
 * Determines if a URL should open in a modal instead of navigating
 */
const shouldOpenModal = (url: string) => {
  return url.includes("about-us") || url.includes("our-story") || url.includes("privacy-policy") || url.includes("terms-and-conditions") || url.includes("shipping-returns-and-refunds") || url.includes("secure-payments") || url.includes("veterans-charities") || url.includes("baerskin-stories") || url.includes("accessibility");
};

/**
 * Gets the appropriate modal content based on URL
 */
const getModalContent = (market: string, url?: string) => {
  if (!url) return null;
  switch (true) {
    case url.includes("about-us") || url.includes("our-story"):
      return <AboutUs market={market} />;
    case url.includes("privacy-policy"):
      return <PrivacyPolicy market={market} />;
    case url.includes("terms-and-conditions"):
      return <TermsConditions market={market} />;
    case url.includes("shipping-returns-and-refunds"):
      return <ShippingReturnsRefunds market={market} />;
    case url.includes("secure-payments"):
      return <SecurePayments market={market} />;
    case url.includes("veterans-charities"):
      return <VeteransCharities market={market} />;
    case url.includes("baerskin-stories"):
      return <BaerskinStories market={market} />;
    case url.includes("accessibility"):
      return <Accessibility market={market} />;
    default:
      return null;
  }
};
interface FooterLinkItemProps {
  name: string;
  url: string;
  market: string;
  badge?: FooterNavigationBadge;
}
const FooterLinkItem = t0 => {
  const $ = _c(27);
  const {
    name,
    url,
    market,
    badge
  } = t0;
  const t = getT(getLanguageForMarket(market), {
    a11y
  });
  const [modalOpen, setModalOpen] = useState(false);
  const pathname = usePathname();
  const [lastPathname, setLastPathname] = useState(pathname);
  if (lastPathname !== pathname) {
    setLastPathname(pathname);
    if (modalOpen) {
      setModalOpen(false);
    }
  }
  const isExternal = /^(https?|mailto|tel|ftp):/i.test(url);
  const openInModal = !isExternal && shouldOpenModal(url);
  let t1;
  if ($[0] !== market || $[1] !== url) {
    t1 = getModalContent(market, url);
    $[0] = market;
    $[1] = url;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const modalContent = t1;
  if (isExternal) {
    let t2;
    if ($[3] !== badge) {
      t2 = badge && <BadgeImage badge={badge} />;
      $[3] = badge;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== name) {
      t3 = <span>{name}</span>;
      $[5] = name;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return <a href={url} target="_blank" rel={/^(mailto|tel):/i.test(url) ? undefined : "noopener noreferrer"} aria-label={t("a11y.openInNewTab", {
      name
    })} className="inline-flex items-center gap-2 text-base">{t2}{t3}</a>;
  }
  if (openInModal) {
    let t2;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = () => setModalOpen(true);
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== badge) {
      t3 = badge && <BadgeImage badge={badge} />;
      $[8] = badge;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== name) {
      t4 = <span>{name}</span>;
      $[10] = name;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== modalContent || $[13] !== modalOpen) {
      t5 = <Modal open={modalOpen} toggle={setModalOpen} contentClassName="max-w-4xl">{modalContent}</Modal>;
      $[12] = modalContent;
      $[13] = modalOpen;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return <><button onClick={t2} className="inline-flex items-center gap-2 text-left text-base hover:text-gray-300" type="button" aria-label={t("a11y.openInModal", {
        name
      })}>{t3}{t4}</button>{t5}</>;
  }
  let t2;
  if ($[15] !== market || $[16] !== pathname || $[17] !== url) {
    const resolvedUrl = localizeMarketPath(url.replace("%%MARKET%%", marketBaseOf(market)), market);
    t2 = resolvedUrl.includes("contact-us") ? withReturnPath({
      href: resolvedUrl,
      returnPath: pathname
    }) : resolvedUrl;
    $[15] = market;
    $[16] = pathname;
    $[17] = url;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  const href = t2;
  let t3;
  if ($[19] !== badge) {
    t3 = badge && <BadgeImage badge={badge} />;
    $[19] = badge;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== name) {
    t4 = <span>{name}</span>;
    $[21] = name;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== href || $[24] !== t3 || $[25] !== t4) {
    t5 = <Link href={href} prefetch={false} className="inline-flex items-center gap-2 text-base">{t3}{t4}</Link>;
    $[23] = href;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
};
export const FooterListItemWithModal = t0 => {
  const $ = _c(17);
  const {
    id,
    market,
    name,
    i18nKey,
    subitems,
    external
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== external || $[1] !== i18nKey || $[2] !== id || $[3] !== market || $[4] !== name || $[5] !== subitems) {
    const t = getT(getLanguageForMarket(market), {
      footer,
      a11y
    });
    const visibleSubitems = subitems?.filter(t5 => {
      const {
        markets
      } = t5;
      return !markets || markets.includes(marketBaseOf(market));
    });
    t3 = "text-left text-white";
    t4 = <h5 className="leading-heading mb-2 text-lg font-semibold lg:mb-3">{i18nKey ? t(i18nKey as Parameters<typeof t>[0]) : name}</h5>;
    t1 = "flex flex-col gap-3";
    t2 = visibleSubitems?.map((t6, i) => {
      const {
        name: name_0,
        i18nKey: i18nKey_0,
        url,
        action,
        badge
      } = t6;
      const label = i18nKey_0 ? t(i18nKey_0 as Parameters<typeof t>[0]) : name_0;
      return <li key={`footer-list-subitem-${id}-${i}`} className="my-0! whitespace-nowrap">{action === "cookie-preferences" ? <CookiePreferencesButton name={label} market={market} /> : /^(https?|mailto|tel|ftp):/i.test(url) || external ? <a href={url} target="_blank" rel={/^(mailto|tel):/i.test(url) ? undefined : "noopener noreferrer"} aria-label={t("a11y.openInNewTab", {
          name: label
        })} className="inline-flex items-center gap-2 text-base">{badge && <BadgeImage badge={badge} />}<span>{label}</span></a> : <FooterLinkItem name={label} url={url} market={market} badge={badge} />}</li>;
    });
    $[0] = external;
    $[1] = i18nKey;
    $[2] = id;
    $[3] = market;
    $[4] = name;
    $[5] = subitems;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t2) {
    t5 = <ul className={t1}>{t2}</ul>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};